import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Subject, Subscription, of } from 'rxjs';
import { takeUntil, mergeMap, switchMap, tap } from 'rxjs/operators';
// Models
import {
  SendDownloadInvoice,
  PaymentPreview,
  CustomInvoiceFieldValue,
  SettingsInvoice,
  RecurringInvoiceTip,
} from 'src/app/invoice/models/invoice.model';
import { SendDownloadEstimate } from 'src/app/invoice/models/estimate.model';
import { Settings } from 'src/app/shared/models/settings/settings.model';
import {
  Customer,
  CustomerAddress,
  CustomerPhone,
} from 'src/app/shared/models/customer.model';
import { LocationModel } from 'src/app/settings/models/location/location-details.model';
import {
  InvoiceAttachment,
  ManualInvoicePartialPaySetting,
  SendReceiptInvoice,
} from 'src/app/invoice/models/invoice.model';
import { User } from 'src/app/shared/models/user.model';
import { TermsAndConditionModel } from 'src/app/settings/models/terms-conditions-policy-model';
// Services
import { AlertService } from 'src/app/core/alert.service';
import { AuthService } from 'src/app/core/auth.service';
import { UserService } from 'src/app/shared/services/user.service';
import { AccountService } from 'src/app/shared/services/account.service';
import { CustomerManagementService } from 'src/app/shared/services/customer.service';
import { ServiceWorkOrderService } from 'src/app/shared/services/service-work-order.service';
import { InvoiceService } from 'src/app/invoice/services/invoice-service.service';
import { EstimateService } from 'src/app/invoice/services/estimate-service.service';
import { RecurringInvoiceService } from 'src/app/invoice/services/recurring-invoice.service';
// Components
import { SendMultipleInvoiceComponent } from '../send-multiple-invoice/send-multiple-invoice.component';
import { InvoiceAutoSendScheduleComponent } from '../invoice-auto-send-schedule/invoice-auto-send-schedule.component';
import { PaymentOptionsComponent } from 'src/app/shared/components/payment-options_revamp/payment-options.component';
import { PrintPreviewComponent } from './components/print-preview/print-preview.component';
import { RecurringInvoiceCancellationComponent } from 'src/app/invoice/customer-invoice-details/recurring-invoice-cancellation/subscription-cancellation.component';
import { PauseRecurringInvoiceDialogComponent } from 'src/app/invoice/invoice-detail-view/pause-recurring-invoice-dialog/pause-subscription-dialog.component';
import { RecurringInvoiceSuspensionFormComponent } from 'src/app/invoice/invoice-detail-view/recurring-invoice-suspension-form/subscription-suspension-form.component';
import { PaymentService } from 'src/app/shared/services/payments.service';

interface Response {
  [key: string]: any;
}

@Component({
  selector: 'app-invoice-summary',
  templateUrl: './invoice-summary.component.html',
  styleUrls: ['./invoice-summary.component.scss'],
})
export class InvoiceSummaryComponent implements OnInit, OnDestroy {
  @Input() invoiceType: 'manual' | 'estimate' | 'recurring' = 'manual';
  @Input() invoiceNumber: string = '';
  @Input() recurringParent: string = '';
  @Input() isRecurringInvoiceChild: boolean = false;
  @Input() customerProfileId: number;
  @Output() subscriptionUpdated = new EventEmitter<number>();

  readonly unsubscribe$ = new Subject();

  isViewLoad: boolean = false;
  isInitLoad: boolean = false;
  isSending: boolean = false;

  businessInfo: Settings = new Settings();
  customer: Customer;
  customerAddresses: CustomerAddress[];
  customerContacts: CustomerPhone[];
  customerBusinessContact: any;
  customerSignature: any;
  locationData: LocationModel;
  invoice: any;
  recurringInvoice: any;
  invoiceSettings: SettingsInvoice = new SettingsInvoice();
  invoiceAttachments: InvoiceAttachment[];
  invoicePolicies: TermsAndConditionModel[];
  invoicePayments: any = [];
  invoicePaid: PaymentPreview = new PaymentPreview();
  invoiceNotes: any;
  // paymentData: PaymentPreview = new PaymentPreview();
  partialPayment: ManualInvoicePartialPaySetting;
  customFieldValues: CustomInvoiceFieldValue[];
  userInfo: User;
  workOrder: any;
  displayBalance: any;
  paymentConfig: any = {
    showCashPayment: true,
    hideManualPaymentOption: false,
    showACHPaymentOption: true,
    showCreditCardOption: true,
  };

  constructor(
    private modalRef: NgbActiveModal,
    private modalService: NgbModal,
    private alertService: AlertService,
    private authService: AuthService,
    private invoiceService: InvoiceService,
    private estimateService: EstimateService,
    private recurringService: RecurringInvoiceService,
    private userService: UserService,
    private accountService: AccountService,
    private customerService: CustomerManagementService,
    private workOrderService: ServiceWorkOrderService,
    private paymentService: PaymentService,
  ) {}

  ngOnInit(): void {
    this.isInitLoad = true;
    this.userInfo = JSON.parse(this.authService.getUserInfo());

    if (this.invoiceType === 'estimate') {
      this.getEstimateData();
      return;
    }

    this.getSummaryData();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getEstimateData(): void {
    this.estimateService
      .getEstimate(this.invoiceNumber)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((estimate: any) => (this.invoice = estimate)),
        mergeMap(() => {
          let estimateData: any = {
            businessInfo: this.userService.getMerchant('1'),
            invoicePolicies: this.accountService.getTtcps(),
            invoiceItems:
              this.estimateService.getEstimateDetailsByEstimateNumber(
                this.invoiceNumber
              ),
            invoicePaid:
              this.invoiceService.getPaidManualInvoiceByInvoiceNumber(
                this.invoiceNumber
              ),
            invoiceAttachments: this.estimateService.getEstimateDocuments(
              this.invoiceNumber
            ),
            customer: this.customerService.getCustomer(this.invoice.customerId),
            customerAddresses: this.customerService.getCustomerAddresses(
              this.invoice.customerId
            ),
            customerContacts: this.customerService.getCustomerContactPersons(
              this.invoice.customerId
            ),
            customFieldValues:
              this.invoiceService.getAllCustomFieldValuesByInvoiceNumber(
                this.invoiceNumber
              ),
            locationData: this.authService.getLocationById(
              this.invoice.locationId
            ),
            createdBy: this.accountService.getUser(this.invoice.createdBy),
            updatedBy: this.accountService.getUser(this.invoice.updatedBy),
          };

          if (this.invoice.invoiceNumber) {
            estimateData = {
              ...estimateData,
              manualInvoices: this.invoiceService.getManualInvoice(
                this.invoice.invoiceNumber
              ),
            };
          }

          return forkJoin({ ...estimateData });
        })
      )
      .subscribe(
        (data: Response) => {
          for (const [key, value] of Object.entries(data)) {
            if (key === 'locationData') {
              this.locationData = value.locationComplete;
              continue;
            }

            if (key === 'invoicePolicies') {
              this.invoicePolicies = value.filter((policy) =>
                this.invoice.tcpIds.includes(policy.TCPId)
              );
              continue;
            }

            if (key === 'invoiceItems') {
              this.invoice.invoiceItems = value;
              continue;
            }

            if (key === 'createdBy') {
              this.invoice.createdByUserName = value.EmployeeName;
              continue;
            }

            if (key === 'updatedBy') {
              this.invoice.updatedByUserName = value.EmployeeName;
              continue;
            }

            if (key === 'manualInvoices') {
              this.invoice.manualInvoice = value;
              continue;
            }

            if (key === 'customFieldValues') {
              this.customFieldValues = value.filter((field) => field.isActive);
              continue;
            }

            this[key] = value;
          }
        },
        (error) => {
          console.log(error);
          this.isInitLoad = false;
        },
        () => {
          this.isInitLoad = false;
        }
      );
  }

  getSummaryData(): void {
    this.invoiceService
      .getManualInvoice(this.invoiceNumber)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((invoice: any) => (this.invoice = invoice)),
        mergeMap(() => {
          if (this.invoiceType === 'recurring') {
            return this.recurringService.getRecurringInvoiceConfigurationByInvoiceNumber(
              this.invoiceNumber
            );
          }
          return of(null);
        }),
        switchMap((config: any) => {
          let summaryData: any = {
            businessInfo: this.userService.getMerchant('1'),
            invoicePolicies: this.accountService.getTtcps(),
            invoiceSettings: this.invoiceService.getSettingsInvoice(),
            invoiceItems:
              this.invoiceService.getManualInvoiceDetailsByInvoiceNumber(
                this.invoiceNumber
              ),
            invoiceAttachments: this.invoiceService.getManualInvoiceDocuments(
              this.invoiceNumber
            ),
            invoicePayments: this.invoiceService.getManualInvoicePayments(
              this.invoiceNumber
            ),
            invoicePaid:
              this.invoiceService.getPaidManualInvoiceByInvoiceNumber(
                this.invoiceNumber
              ),
            invoiceNotes: this.invoiceService.getAllInvoiceNotesByInvoiceNumber(
              this.invoiceNumber
            ),
            partialPayment:
              this.invoiceService.getPartialPaySettingByInvoiceNumber(
                this.invoiceNumber
              ),
            customFieldValues:
              this.invoiceService.getAllCustomFieldValuesByInvoiceNumber(
                this.invoiceNumber
              ),
            customerSignature:
              this.invoiceService.getCustomerSigByInvoiceNumber(
                this.invoiceNumber
              ),
            customer: this.customerService.getCustomer(this.invoice.customerId),
            customerAddresses: this.customerService.getCustomerAddresses(
              this.invoice.customerId
            ),
            customerContacts: this.customerService.getCustomerPhones(
              this.invoice.customerId
            ),
            customerBusinessContact:
              this.customerService.getCustomerContactPersonById(
                this.invoice.businessCustomerContactPersonId
              ),
            workOrder: this.workOrderService.getWorkOrderByInvoice(
              this.invoiceNumber
            ),
            locationData: this.authService.getLocationById(
              this.invoice.locationId
            ),
          };

          if (config) {
            summaryData = {
              ...summaryData,
              recurringInvoice: this.recurringService.getRecurringInvoiceById(
                config.recurringInvoiceId
              ),
              recurringManualInvoices:
                this.recurringService.getRecurringInvoicesByRecurringId(
                  config.recurringInvoiceId
                ),
            };
          }

          return forkJoin({ ...summaryData });
        })
      )
      .subscribe(
        (data: Response) => {
          for (const [key, value] of Object.entries(data)) {
            if (key === 'locationData') {
              this.locationData = value.locationComplete;

              this.paymentConfig.showACHPaymentOption =
                this.locationData.ACHEnabled;
              this.paymentConfig.showCreditCardOption =
                this.locationData.CCEnabled;

              if (this.isRecurringInvoiceChild && this.recurringInvoice) {
                this.paymentConfig = {
                  showCashPayment:
                    !this.recurringInvoice.configuration.isAutoCharge,
                  hideManualPaymentOption:
                    this.recurringInvoice.configuration.isAutoCharge,
                  showACHPaymentOption:
                    !this.recurringInvoice.configuration.isAutoCharge &&
                    this.locationData.ACHEnabled,
                  showCreditCardOption: true,
                };
              }

              if (this.invoice.subscriptionId) {
                this.paymentConfig = {
                  showACHPaymentOption: false,
                  showCashPayment: false,
                  hideManualPaymentOption: true,
                };
              }
              continue;
            }

            if (key === 'invoicePolicies') {
              this.invoicePolicies = value.filter((policy) =>
                this.invoice.tcpIds.includes(policy.TCPId)
              );
              continue;
            }

            if (key === 'invoiceItems') {
              this.invoice.invoiceItems = value;
              this.invoice.invoiceDate = !this.invoice.isDraft
                ? this.invoice.invoiceDate
                : new Date();
              continue;
            }

            if (key === 'recurringManualInvoices') {
              this.recurringInvoice.invoices = value;
            }

            if (key === 'customFieldValues') {
              this.customFieldValues = value.filter((field) => field.isActive);
              continue;
            }

            this[key] = value;
          }
        },
        (error) => {
          console.log(error);
          this.isInitLoad = false;
        },
        () => {
          this.setPartial();
          this.isInitLoad = false;
        }
      );
  }

  setPartial(): void {
    this.displayBalance = this.invoice.balance;

    if (this.invoicePayments.length) {
      this.invoicePayments.forEach((payment) => {
        this.displayBalance = this.displayBalance - payment.amountPaid;
      });
    } else {
      //if no payments yet, display the allowed partial payment as Amount Due
      if (!!this.partialPayment) {
        if (this.partialPayment.partialDueType === '%') {
          this.displayBalance = Math.fround(
            this.invoice.balance * (this.partialPayment.partialDue / 100)
          );
        } else {
          this.displayBalance = this.partialPayment.partialDue;
        }
      }
    }
  }

  getInvoiceType(): string {
    switch (this.invoiceType) {
      case 'recurring':
        return 'Recurring Invoice';
      case 'estimate':
        return 'Estimate';
      default:
        return 'Invoice';
    }
  }

  canPayPartial(): boolean {
    return (
      (this.partialPayment && this.partialPayment.partialDue) && 
      ['Partial', 'Paid', 'Voided', 'Refunded'].indexOf(this.invoice.status) ===
        -1
    );
  }

  onModalClose(data?: any) {
    this.modalRef.close(data);
  }

  onPrintPreview(): void {
    let previewDiag = this.modalService.open(PrintPreviewComponent);

    previewDiag.componentInstance.invoiceType = this.invoiceType;
    previewDiag.componentInstance.invoice = this.invoice;
    previewDiag.componentInstance.invoicePolicies = this.invoicePolicies;
    previewDiag.componentInstance.customer = this.customer;
    previewDiag.componentInstance.customerAddresses = this.customerAddresses;
    previewDiag.componentInstance.customerSignature = this.customerSignature;
    previewDiag.componentInstance.businessInfo = this.businessInfo;
    previewDiag.componentInstance.locationData = this.locationData;
    previewDiag.componentInstance.customFieldValues = this.customFieldValues;
    previewDiag.componentInstance.paymentData = this.invoicePaid;
    previewDiag.componentInstance.partialPayment = this.partialPayment;
    previewDiag.componentInstance.workOrder = this.workOrder;
    previewDiag.componentInstance.invoicePayments = this.invoicePayments;

    this.onModalClose();
  }

  onDeleteInvoice(): void {
    this.alertService.alert({
      title: 'Confirmation',
      text: `Are you sure you want to delete this ${this.getInvoiceType().toLowerCase()}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      showLoaderOnConfirm: true,
      preConfirm: () => {
        switch (this.invoiceType) {
          case 'estimate':
            this.onDeleteEstimate();
            break;
          case 'recurring':
            this.onDeleteRecurring();
            break;
          default:
            this.onDeleteManual();
            break;
        }
      },
    });
  }

  onDeleteManual(): Subscription {
    return this.invoiceService
      .deleteManualInvoice(this.invoiceNumber, this.userInfo.UserId)
      .subscribe(
        (result) => {
          if (result) {
            this.alertService.alertSuccess({
              title: 'Success',
              text: 'Invoice successfully deleted.',
            });
            this.onModalClose(true);
          }
        },
        (error: any) => {
          this.alertService.alertError({
            title: 'Error',
            text: error.error.text,
          });
        }
      );
  }

  onDeleteRecurring(): Subscription {
    return this.recurringService
      .deleteRecurringInvoicesByRecurringId(
        this.recurringInvoice.id,
        parseInt(this.userInfo.UserId)
      )
      .subscribe(
        (result) => {
          if (result) {
            this.alertService.alertSuccess({
              title: 'Success',
              text: 'Recurring Invoice successfully deleted.',
            });
            this.onModalClose(true);
          }
        },
        (error: any) => {
          this.alertService.alertError({
            title: 'Error',
            text: error.error.text,
          });
        }
      );
  }

  onDeleteEstimate(): Subscription {
    return this.estimateService
      .deleteEstimate(this.invoiceNumber, parseInt(this.userInfo.UserId))
      .subscribe(
        (result) => {
          if (result) {
            this.alertService.alertSuccess({
              title: 'Success',
              text: 'Estimate successfully deleted.',
            });
            this.onModalClose(true);
          }
        },
        (error: any) => {
          this.alertService.alertError({
            title: 'Error',
            text: error.error.text,
          });
        }
      );
  }

  onSelectPayment(): void {
    let paymentDiag = this.modalService.open(PaymentOptionsComponent, {
      backdrop: 'static',
    });

    paymentDiag.componentInstance.isInvoiceSummary = true;
    paymentDiag.componentInstance.canPayPartial = this.canPayPartial();
    paymentDiag.componentInstance.userInfo = this.userInfo;
    paymentDiag.componentInstance.invoice = this.invoice;
    paymentDiag.componentInstance.invoiceSettings = this.invoiceSettings;
    paymentDiag.componentInstance.customer = this.customer;
    paymentDiag.componentInstance.customerBusinessContact =
      this.customerBusinessContact;
    paymentDiag.componentInstance.customerAddresses = this.customerAddresses;
    paymentDiag.componentInstance.customerSignature = this.customerSignature;
    paymentDiag.componentInstance.businessInfo = this.businessInfo;
    paymentDiag.componentInstance.locationData = this.locationData;
    paymentDiag.componentInstance.workOrder = this.workOrder;
    paymentDiag.componentInstance.displayBalance = this.displayBalance;
    paymentDiag.componentInstance.hideManualPaymentOption =
      this.paymentConfig.hideManualPaymentOption;
    paymentDiag.componentInstance.showACHPaymentOption =
      this.paymentConfig.showACHPaymentOption;
    paymentDiag.componentInstance.showCashPayment =
      this.paymentConfig.showCashPayment;
    paymentDiag.componentInstance.showCreditCardOption =
      this.paymentConfig.showCreditCardOption;
    paymentDiag.componentInstance.recurringInvoiceConfig =
      this.recurringInvoice?.configuration;

    this.onModalClose();
  }

  onSendMail(): void {
    this.isSending = true;

    switch (this.invoiceType) {
      case 'estimate':
        this.onSendEstimateEmail();
        break;
      default:
        this.onSendManualEmail();
        break;
    }
  }

  onSendManualEmail(): void {
    let mailInvoice = new SendDownloadInvoice();
    const address = this.customerAddresses.find((e) => e.IsDefault);

    mailInvoice.InvoiceNumber = this.invoiceNumber;
    mailInvoice.CustomerEmail = this.customer.EmailAddress;

    mailInvoice.CustomerName =
      this.customer.CustomerTypeId === 2
        ? this.customer.BusinessName
        : `${this.customer.FirstName} ${this.customer.LastName}`;

    mailInvoice.BusinessName =
      this.businessInfo.BusinessName +
      (this.locationData ? ` - ${this.locationData.BranchName}` : '');
    mailInvoice.BusinessLogo = this.locationData.Logo
      ? `data:image/png;base64,${this.locationData.Logo}`
      : this.businessInfo.Logo;
    mailInvoice.BusinessAddress = this.locationData
      ? `${
          this.locationData.Address1 ? this.locationData.Address1 + ',' : ''
        } ${this.locationData.City ? this.locationData.City + ',' : ''} ${
          this.locationData.Province ? this.locationData.Province + ',' : ''
        } ${this.locationData.StateId || ''} ${
          this.locationData.PostalCode || ''
        } ${this.locationData.CountryCode || ''}`
      : `${
          this.businessInfo.Address1 ? this.businessInfo.Address1 + ',' : ''
        } ${this.businessInfo.City ? this.businessInfo.City + ',' : ''} ${
          this.businessInfo.Province ? this.businessInfo.Province + ',' : ''
        } ${this.businessInfo.StateId || ''} ${
          this.businessInfo.PostalCode || ''
        } ${this.businessInfo.CountryCode || ''}`;

    if (address) {
      mailInvoice.CustomerAddress = `${address.StreetAddress || ''} ${
        address.City || ''
      } ${address.State || ''} ${address.ZipCode || ''}`;
    }

    if (this.customerBusinessContact) {
      mailInvoice.CustomerEmail =
        this.customerBusinessContact.EmailAddress || this.customer.EmailAddress;
    }

    this.invoiceService.sendInvoice(mailInvoice).subscribe(() => {
      this.alertService.alertSuccess({
        title: 'Success!',
        text: 'Invoice has been successfully sent!',
      });

      this.isSending = false;
    });
  }

  onSendEstimateEmail(): void {
    let mailEstimate = new SendDownloadEstimate();
    const address = this.customerAddresses.find((e) => e.IsDefault);

    mailEstimate.EstimateNumber = this.invoice.estimateNumber;
    mailEstimate.CustomerEmail = this.customer.EmailAddress;
    mailEstimate.BusinessName = this.businessInfo.BusinessName;
    mailEstimate.BusinessLogo = this.businessInfo.Logo;

    mailEstimate.CustomerName =
      this.customer.CustomerTypeId === 2
        ? this.customer.BusinessName
        : `${this.customer.FirstName} ${this.customer.LastName}`;

    mailEstimate.BusinessAddress = `${
      this.businessInfo.Address1 ? this.businessInfo.Address1 + ',' : ''
    } ${this.businessInfo.City ? this.businessInfo.City + ',' : ''} ${
      this.businessInfo.Province ? this.businessInfo.Province + ',' : ''
    } ${this.businessInfo.StateId || ''} ${
      this.businessInfo.PostalCode || ''
    } ${this.businessInfo.CountryCode || ''}`;

    if (address) {
      mailEstimate.CustomerAddress = `${address.StreetAddress || ''} ${
        address.City || ''
      } ${address.State || ''} ${address.ZipCode || ''}`;
    }

    if (this.customerBusinessContact) {
      mailEstimate.CustomerEmail =
        this.customerBusinessContact.EmailAddress || this.customer.EmailAddress;
    }

    this.estimateService.sendEstimate(mailEstimate).subscribe(() => {
      this.alertService.alertSuccess({
        title: 'Success!',
        text: 'Estimate has been successfully sent!',
      });

      this.isSending = false;
    });
  }

  onSendReceipt(): void {
    this.isSending = true;

    const latestPayment = this.invoicePayments[this.invoicePayments.length - 1];
    const payload = {
      emailAddress: this.customer.EmailAddress,
      mobileNumber: null,
      paymentMethod: latestPayment.paymentMethodTypeName,
      receiptNumber: latestPayment.receiptNumber,
    }

    this.paymentService.sendReceipt(payload).subscribe({
      next: (res) => {
        if (res.toUpperCase() === 'SUCCESS') {
          this.alertService.alertSuccess({
            title: 'Success!',
            text: 'Receipt has been successfully sent!',
          });
        } else {
          this.alertService.alertError({
            title: 'Error!',
            text: res,
          });
        }
      },
      error: (error) => {
        this.alertService.alertError({
          title: 'Error!',
          text: error,
        });
        this.isSending = false;
      },
      complete: () => {
        this.isSending = false;
      }
    });
  }

  // TODO: All methods below are for refactoring
  onSendSchedule(): void {
    let dialog = this.modalService.open(InvoiceAutoSendScheduleComponent, {
      size: 'sm',
      backdrop: 'static',
    });

    dialog.componentInstance.manualInvoiceModel = this.invoice;
  }

  onSendMultiple(status: string): void {
    if (status === 'SelectInvoice') {
      let dialog = this.modalService.open(SendMultipleInvoiceComponent, {
        size: 'xl',
      });

      dialog.componentInstance.customerId = this.invoice['customerId'];
      dialog.componentInstance.customerName =
        this.customer.CustomerTypeId === 1
          ? this.customer.FirstName + ' ' + this.customer.LastName
          : this.customer.BusinessName;

      dialog.result.then((res) => {
        if (res) {
          this.alertService.alertSuccess({
            title: 'Success!',
            text: 'Invoice has been successfully sent!',
          });
        }
      });
    } else {
      this.isSending = true;
      this.invoiceService
        .sendMultipleInvoicesByStatus(this.invoice['customerId'], status)
        .subscribe(() => {
          this.alertService.alertSuccess({
            title: 'Success!',
            text: 'Invoice has been successfully sent!',
          });

          this.isSending = false;
        });
    }
  }

  onSubscriptionAction(action: string): void {
    this.isSending = true;

    switch (action) {
      case 'Cancel':
        this.onCancelSubscription();
        break;
      case 'Pause':
      case 'Resume':
        this.onPauseResumeSubscription(action.toLowerCase());
        break;
      case 'Suspend':
        this.onSuspendSubscription();
        break;
      case 'Activate':
      case 'Archive':
        this.onActivateArchiveInvoice(action.toLowerCase());
        break;
      case 'View':
        this.onViewSuspendDetails();
        break;
      default:
        this.onDeactivateAutoTip();
        break;
    }
  }

  onCancelSubscription(): void {
    let dialog = this.modalService.open(RecurringInvoiceCancellationComponent, {
      size: 'md',
      keyboard: false,
      backdrop: 'static',
    });

    dialog.componentInstance.recurringInvoiceModel = this.recurringInvoice;
    dialog.componentInstance.invoiceModel = this.invoice;
    dialog.componentInstance.status = 'Approved';
    dialog.componentInstance.displayApprovalNotes = true;

    dialog.result.then((res) => {
      if (res) {
        // this.subscriptionUpdated.emit();
        this.onModalClose(true);
      }
      this.isSending = false;
    });
  }

  onPauseResumeSubscription(action: string): void {
    let dialog = this.modalService.open(PauseRecurringInvoiceDialogComponent, {
      size: 'sm',
      keyboard: false,
      backdrop: 'static',
    });

    dialog.result.then((res) => {
      if (res) {
        this.recurringService[action]({
          recurringInvoiceId: this.recurringInvoice.id,
          userId: this.userInfo.UserId,
          resumeDate: res,
        }).subscribe((res) => {
          if (res) {
            // this.subscriptionUpdated.emit();
            this.alertService.alertSuccess({
              title: 'Success',
              text: 'Recurring Invoice successfully updated!',
            });
          } else {
            this.alertService.alertError({
              title: 'Error!',
              text: 'Something went wrong, please contact administrator.',
            });
          }
          this.onModalClose(true);
        });
      }
      this.isSending = false;
    });
  }

  onSuspendSubscription(): void {
    let dialog = this.modalService.open(
      RecurringInvoiceSuspensionFormComponent,
      {
        size: 'sm',
        backdrop: 'static',
      }
    );

    dialog.componentInstance.recurringInvoiceId = this.recurringInvoice.id;
    dialog.componentInstance.source = 'recurring_invoice';

    dialog.result.then((res) => {
      if (res) {
        // this.subscriptionUpdated.emit();
        this.onModalClose(true);
      }
      this.isSending = false;
    });
  }

  onActivateArchiveInvoice(action: string): void {
    this.alertService.alert({
      title: 'Confirmation',
      text: `Are you sure you want to ${action} this recurring invoice?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${action} it!`,
      showLoaderOnConfirm: true,
      preConfirm: () => {
        let payload: any = {
          userId: this.userInfo.UserId,
          recurringInvoiceId: this.recurringInvoice.id,
        };

        if (action === 'activate') {
          payload.resumeDate = true;
        }

        return this.recurringService[action](payload).subscribe(
          (result) => {
            if (result) {
              this.alertService.alertSuccess({
                title: 'Success',
                text: 'Recurring Invoice successfully updated.',
              });

              this.isSending = false;
              this.onModalClose(true);
            }
          },
          () => {
            this.alertService.alertError({
              title: 'Error!',
              text: 'Something went wrong, please contact administrator.',
            });

            this.isSending = false;
          }
        );
      },
    });
  }

  onViewSuspendDetails(): void {
    let dialog = this.modalService.open(
      RecurringInvoiceSuspensionFormComponent,
      {
        size: 'sm',
        backdrop: 'static',
      }
    );

    dialog.componentInstance.recurringInvoiceId = this.recurringInvoice.id;
    dialog.componentInstance.viewOnly = true;
    dialog.componentInstance.source = 'recurring_invoice';

    dialog.result.then((res) => {
      if (res) {
        // this.subscriptionUpdated.emit();
        this.onModalClose(true);
      }
      this.isSending = false;
    });
  }

  onDeactivateAutoTip() {
    const payload = new RecurringInvoiceTip();

    payload.Tip = this.recurringInvoice.configuration.tip;
    payload.TipType = this.recurringInvoice.configuration.tipType;
    payload.RecurringInvoiceId = this.recurringInvoice.id;
    payload.UpdatedBy = this.recurringInvoice.id;
    payload.IsRepeatTipOnSucceedingInvoices = false;

    this.alertService.alert({
      title: 'Confirmation',
      text: `Are you sure you want to deactivate auto-tip for the future payments?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, deactivate it!`,
      showLoaderOnConfirm: true,
      preConfirm: () => {
        return this.recurringService
          .setRecurringInvoiceTipConfiguration(payload)
          .subscribe(
            (result) => {
              if (result) {
                this.alertService.alertSuccess({
                  title: 'Recurring Invoice Tip',
                  text: 'Auto-tip for future payments is now deactivated!',
                });
                this.isSending = false;
                this.onModalClose(true);
              }
            },
            () => {
              this.isSending = false;
            }
          );
      },
    });
  }
}
